import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSupabaseClient } from "@/lib/supabase";
import { playNotificationSound } from "@/lib/notificationSound";
import type { Notification } from "../types";
import type { NotificationRow } from "@/types/supabase";
import type { SupabaseClient } from "@supabase/supabase-js";

export const notificationsQueryKey = ["notifications"] as const;

// Supabase types are hand-authored and don't include new tables yet.
// Cast to base SupabaseClient to bypass the typed table constraint.
function db(client: SupabaseClient) {
  return (client as SupabaseClient<Record<string, never>>).from("notifications");
}

export function useNotifications() {
  const { client, isReady } = useSupabaseClient();
  const queryClient = useQueryClient();
  const prevUnreadRef = useRef<number | null>(null);

  // ─── Query ──────────────────────────────────────────────────────────────────
  const query = useQuery({
    queryKey: notificationsQueryKey,
    enabled: isReady,
    queryFn: async () => {
      const { data, error } = await db(client)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as NotificationRow[];
    },
  });

  // ─── Mark single read ────────────────────────────────────────────────────────
  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db(client)
        .update({ read: true, read_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    },
  });

  // ─── Mark all read ───────────────────────────────────────────────────────────
  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await db(client)
        .update({ read: true, read_at: new Date().toISOString() })
        .eq("read", false);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    },
  });

  // ─── Realtime subscription ───────────────────────────────────────────────────
  // Uses the authenticated client so RLS applies — only receives own rows.
  useEffect(() => {
    if (!isReady) return;

    const channel = client
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        () => {
          void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications" },
        () => {
          void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
        },
      )
      .subscribe();

    return () => {
      void client.removeChannel(channel);
    };
  }, [isReady, client, queryClient]);

  // ─── Sound on new unread ─────────────────────────────────────────────────────
  const notifications: Notification[] = (query.data ?? []) as Notification[];
  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    if (prevUnreadRef.current !== null && unreadCount > prevUnreadRef.current) {
      playNotificationSound();
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading: query.isLoading,
    markRead: (id: string) => markRead.mutate(id),
    markAllRead: () => markAllRead.mutate(),
  };
}
